import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, onSnapshot, getDoc, writeBatch, updateDoc, setDoc, collection, getDocs } from 'firebase/firestore';
import { db, auth } from '../lib/firebase';
import { TopNav } from '../components/TopNav';
import { PlayerList } from '../components/PlayerList';
import { endGame } from '../lib/gameService';
import { Zap, Plus, X, Trash2, StopCircle, ChevronLeft, ArrowLeftRight, Layers } from 'lucide-react';
import clsx from 'clsx';
import { SessionSubNav } from '../components/SessionSubNav';
import { PlayerSelector } from '../components/PlayerSelector';
import { useLanguage } from '../contexts/LanguageContext';


// ─── Financial Row ─────────────────────────────────────────────────
function FinancialRow({ item, onDelete, accentColor = 'emerald' }) {
    const { formatCurrency, language } = useLanguage();
    return (
        <div className="flex items-center justify-between py-2 px-3 rounded-lg bg-slate-800/60 border border-white/5 gap-2">
            <div className="flex-1 min-w-0">
                <div className="text-xs font-bold text-slate-200 truncate">{item.description || '—'}</div>
                <div className="text-[10px] text-slate-500 mt-0.5">
                    {item.source === 'T1' ? 'Table 1' : item.source === 'T2' ? 'Table 2' : ''}
                    {item.timestamp ? ` · ${new Date(item.timestamp).toLocaleTimeString(language === 'he' ? 'he-IL' : 'en-US', { hour: '2-digit', minute: '2-digit' })}` : ''}
                </div>
            </div>
            <span className={`text-sm font-black tabular-nums text-${accentColor}-400`} dir="ltr">{formatCurrency(item.amount)}</span>
            {onDelete && (
                <button onClick={() => onDelete(item)} className="text-slate-600 hover:text-rose-400 transition-colors flex-shrink-0 ml-1">
                    <Trash2 className="w-3.5 h-3.5" />
                </button>
            )}
        </div>
    );
}

// ─── Add Financial Form ────────────────────────────────────────────
function AddFinancialForm({ onAdd, onClose, label, accentColor }) {
    const { t } = useLanguage();
    const [amount, setAmount] = useState('');
    const [desc, setDesc] = useState('');
    const [target, setTarget] = useState('T1');
    const presets = [50, 100, 200, 300, 500];

    const submit = () => {
        const a = parseFloat(amount);
        if (isNaN(a) || a <= 0) return;
        onAdd({ amount: a, description: desc.trim() || label, target });
        onClose();
    };

    return (
        <div className="bg-slate-800/80 border border-white/10 rounded-2xl p-4 space-y-3 mt-2">
            <div className="flex gap-2">
                {['T1', 'T2'].map(t => (
                    <button
                        key={t}
                        onClick={() => setTarget(t)}
                        className={clsx(
                            'flex-1 py-2 rounded-lg text-xs font-black uppercase tracking-wider transition-all border',
                            target === t
                                ? t === 'T1' ? 'bg-blue-500/20 border-blue-400 text-blue-300' : 'bg-purple-500/20 border-purple-400 text-purple-300'
                                : 'bg-slate-900/50 border-slate-700 text-slate-500'
                        )}
                    >{t(t === 'T1' ? 'tableOne' : 'tableTwo')}</button>
                ))}
            </div>
            <div className="flex gap-1.5 flex-wrap">
                {presets.map(p => (
                    <button
                        key={p}
                        onClick={() => setAmount(String(p))}
                        className={clsx(
                            'px-2.5 py-1 rounded-lg text-[11px] font-bold border transition-all',
                            amount === String(p)
                                ? 'bg-amber-500 text-slate-950 border-amber-400'
                                : 'bg-slate-900/60 border-slate-700 text-slate-400 hover:border-amber-500/40'
                        )}
                    >₪{p}</button>
                ))}
            </div>
            <input
                type="number"
                placeholder={`${t('amount')} (₪)`}
                value={amount}
                onChange={e => setAmount(e.target.value)}
                className="w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-2.5 text-white text-sm outline-none focus:border-amber-500 font-mono"
            />
            <input
                placeholder={t('descriptionOptional')}
                value={desc}
                onChange={e => setDesc(e.target.value)}
                className="w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-2.5 text-white text-sm outline-none focus:border-amber-500"
            />
            <div className="flex gap-2">
                <button onClick={onClose} className="flex-1 py-2.5 rounded-xl bg-slate-700 text-slate-300 text-sm font-bold hover:bg-slate-600 transition-colors">{t('cancel')}</button>
                <button onClick={submit} className={clsx('flex-1 py-2.5 rounded-xl text-sm font-black transition-all', `bg-${accentColor}-600 hover:bg-${accentColor}-500 text-white`)}>{t('add')} {label}</button>
            </div>
        </div>
    );
}

// ─── Master Ledger ─────────────────────────────────────────────────
export const MasterLedger = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { t, direction, language, formatCurrency } = useLanguage();

    const user = auth.currentUser;
    const hostId = user?.uid;

    const [gameData, setGameData] = useState(null);
    const [t1Data, setT1Data] = useState(null);
    const [t2Data, setT2Data] = useState(null);
    const [showAddExpense, setShowAddExpense] = useState(false);
    const [showAddCollected, setShowAddCollected] = useState(false);
    const [showEndModal, setShowEndModal] = useState(false);
    const [showPlayerSelector, setShowPlayerSelector] = useState(false);
    const [showTableSelect, setShowTableSelect] = useState(false);
    const [pendingPlayer, setPendingPlayer] = useState(null);
    const [ending, setEnding] = useState(false);
    const [allPlayers, setAllPlayers] = useState([]);

    const parseSession = (data) => {
        if (!data) return null;
        const seatsArray = Array(11).fill(null);
        if (data.tableSeats) {
            Object.keys(data.tableSeats).forEach(seatKey => {
                const index = parseInt(seatKey);
                if (!isNaN(index) && index >= 1 && index <= 11 && data.tableSeats[seatKey]) {
                    seatsArray[index - 1] = data.tableSeats[seatKey];
                }
            });
        }
        return {
            ...data,
            seatedPlayers: seatsArray,
            buyins: data.buyins || [],
            cashouts: data.cashouts || [],
            expenses: data.expenses || [],
            collected: data.collected || []
        };
    };

    useEffect(() => {
        if (!hostId || !id) return;

        let unsub1, unsub2;

        const loadMaster = async () => {
            // Load all available players for the selector
            const playersSnap = await getDocs(collection(db, `hosts/${hostId}/players`));
            setAllPlayers(playersSnap.docs.map(d => ({ id: d.id, ...d.data() })));

            // First try looking up as a Game (parent)
            let gameRef = doc(db, `hosts/${hostId}/games/${id}`);
            let gameSnap = await getDoc(gameRef);
            
            let gameIdToUse = id;

            // If not found in games, it might be a child session ID
            if (!gameSnap.exists()) {
                const sessionRef = doc(db, `hosts/${hostId}/sessions/${id}`);
                const sessionSnap = await getDoc(sessionRef);
                if (sessionSnap.exists()) {
                    const sessionData = sessionSnap.data();
                    if (sessionData.gameId) {
                        gameIdToUse = sessionData.gameId;
                        gameRef = doc(db, `hosts/${hostId}/games/${gameIdToUse}`);
                        gameSnap = await getDoc(gameRef);
                    }
                }
            }

            if (!gameSnap.exists()) return;

            const gd = { id: gameSnap.id, ...gameSnap.data() };
            setGameData(gd);

            if (gd.ledgerId) {
                unsub1 = onSnapshot(doc(db, `hosts/${hostId}/sessions/${gd.ledgerId}`), (snap) => {
                    setT1Data(parseSession(snap.data()));
                });
            }
            if (gd.ledgerId2) {
                unsub2 = onSnapshot(doc(db, `hosts/${hostId}/sessions/${gd.ledgerId2}`), (snap) => {
                    setT2Data(parseSession(snap.data()));
                });
            }
        };

        loadMaster();
        return () => {
            if (unsub1) unsub1();
            if (unsub2) unsub2();
        };
    }, [hostId, id]);

    const getLedgerRef = (tableTarget) => {
        const ledgerId = tableTarget === 'T2' ? gameData?.ledgerId2 : gameData?.ledgerId;
        if (!ledgerId) return null;
        return doc(db, `hosts/${hostId}/sessions/${ledgerId}`);
    };

    const handleAddExpense = async ({ amount, description, target }) => {
        const ref = getLedgerRef(target);
        if (!ref) return;
        const currentData = target === 'T2' ? t2Data : t1Data;
        const current = currentData?.expenses || [];
        const newItem = { id: `exp_${Date.now()}`, description, amount, timestamp: new Date().toISOString() };
        await updateDoc(ref, { expenses: [...current, newItem] });
    };

    const handleDeleteExpense = async (item) => {
        // Find which table owns this expense
        const inT1 = (t1Data?.expenses || []).some(e => e.id === item.id);
        const ref = getLedgerRef(inT1 ? 'T1' : 'T2');
        const currentData = inT1 ? t1Data : t2Data;
        if (!ref) return;
        const updated = (currentData?.expenses || []).filter(e => e.id !== item.id);
        await updateDoc(ref, { expenses: updated });
    };

    const handleAddCollected = async ({ amount, description, target }) => {
        const ref = getLedgerRef(target);
        if (!ref) return;
        const currentData = target === 'T2' ? t2Data : t1Data;
        const current = currentData?.collected || [];
        const newItem = { id: `col_${Date.now()}`, description, amount, timestamp: new Date().toISOString() };
        await updateDoc(ref, { collected: [...current, newItem] });
    };

    const handleDeleteCollected = async (item) => {
        const inT1 = (t1Data?.collected || []).some(c => c.id === item.id);
        const ref = getLedgerRef(inT1 ? 'T1' : 'T2');
        const currentData = inT1 ? t1Data : t2Data;
        if (!ref) return;
        const updated = (currentData?.collected || []).filter(c => c.id !== item.id);
        await updateDoc(ref, { collected: updated });
    };

    const handleMovePlayer = async (sourceTableIndex, sourceSlotIndex) => {
        if (!t1Data || !t2Data || !gameData) return;

        const sourceData = sourceTableIndex === 1 ? t1Data : t2Data;
        const destData = sourceTableIndex === 1 ? t2Data : t1Data;

        const sourceLedgerId = sourceTableIndex === 1 ? gameData.ledgerId : gameData.ledgerId2;
        const destLedgerId = sourceTableIndex === 1 ? gameData.ledgerId2 : gameData.ledgerId;

        const playerToMove = sourceData.seatedPlayers[sourceSlotIndex - 1];
        if (!playerToMove) return;

        let nextOpenSlot = -1;
        for (let i = 0; i < 11; i++) {
            if (!destData.seatedPlayers[i]) { nextOpenSlot = i + 1; break; }
        }

        if (nextOpenSlot === -1) {
            alert('השולחן המיועד מלא.');
            return;
        }

        if (!window.confirm(`העבר ${playerToMove.name} לכיסא ${nextOpenSlot} בשולחן ${sourceTableIndex === 1 ? '2' : '1'}?`)) return;

        try {
            const batch = writeBatch(db);
            const sourceRef = doc(db, `hosts/${hostId}/sessions/${sourceLedgerId}`);
            const destRef = doc(db, `hosts/${hostId}/sessions/${destLedgerId}`);

            // 1. Move Buy-ins
            const sBuyins = sourceData.buyins || [];
            const movedBuyins = sBuyins.filter(b => b.playerId === playerToMove.id).map(b => ({
                ...b,
                slotIndex: nextOpenSlot
            }));
            const remainingSourceBuyins = sBuyins.filter(b => b.playerId !== playerToMove.id);
            const dBuyins = destData.buyins || [];

            // 2. Move Cashouts (if any)
            const sCashouts = sourceData.cashouts || [];
            const movedCashouts = sCashouts.filter(c => c.playerId === playerToMove.id).map(c => ({
                ...c,
                slotIndex: nextOpenSlot
            }));
            const remainingSourceCashouts = sCashouts.filter(c => c.playerId !== playerToMove.id);
            const dCashouts = destData.cashouts || [];

            batch.update(sourceRef, { 
                [`tableSeats.${sourceSlotIndex}`]: null,
                buyins: remainingSourceBuyins,
                cashouts: remainingSourceCashouts
            });
            batch.update(destRef, { 
                [`tableSeats.${nextOpenSlot}`]: playerToMove,
                buyins: [...dBuyins, ...movedBuyins],
                cashouts: [...dCashouts, ...movedCashouts]
            });
            await batch.commit();
        } catch (error) {
            console.error('Error moving player in batch:', error);
            alert('Failed to move player. Check console.');
        }
    };

    const handleAddPlayerClick = () => {
        setShowPlayerSelector(true);
    };

    const handlePlayerSelected = (player) => {
        setPendingPlayer(player);
        setShowPlayerSelector(false);
        setShowTableSelect(true);
    };

    const handleTableAssign = async (tableNum) => {
        if (!pendingPlayer || !hostId) return;

        const targetData = tableNum === 1 ? t1Data : t2Data;
        const targetLedgerId = tableNum === 1 ? gameData.table1LedgerId : gameData.table2LedgerId;

        if (!targetLedgerId) return;

        // Find next open slot
        let nextSlot = -1;
        for (let i = 1; i <= 11; i++) {
            if (!targetData.tableSeats?.[i]) {
                nextSlot = i;
                break;
            }
        }

        if (nextSlot === -1) {
            alert(`No open slots on Table ${tableNum}`);
            return;
        }

        try {
            const ledgerRef = doc(db, `hosts/${hostId}/sessions/${targetLedgerId}`);
            await setDoc(ledgerRef, {
                tableSeats: {
                    ...targetData.tableSeats,
                    [nextSlot]: {
                        id: pendingPlayer.id,
                        name: pendingPlayer.name,
                        roles: pendingPlayer.roles || ['Player'],
                        joinedAt: new Date().toISOString()
                    }
                }
            }, { merge: true });

            setShowTableSelect(false);
            setPendingPlayer(null);
        } catch (error) {
            console.error('Error assigning player:', error);
        }
    };

    const handleEndSession = async () => {
        if (!hostId || ending) return;
        setEnding(true);
        try {
            await endGame(hostId, id, {});
            navigate('/dashboard');
        } catch (err) {
            console.error(err);
        } finally {
            setEnding(false);
        }
    };

    // ─── Aggregated numbers ────────────────────────────────────────
    const allExpenses = [
        ...(t1Data?.expenses || []).map(e => ({ ...e, source: 'T1' })),
        ...(t2Data?.expenses || []).map(e => ({ ...e, source: 'T2' }))
    ].sort((a, b) => (a.timestamp || '') > (b.timestamp || '') ? -1 : 1);

    const allCollected = [
        ...(t1Data?.collected || []).map(c => ({ ...c, source: 'T1' })),
        ...(t2Data?.collected || []).map(c => ({ ...c, source: 'T2' }))
    ].sort((a, b) => (a.timestamp || '') > (b.timestamp || '') ? -1 : 1);

    const totalBuyins = [...(t1Data?.buyins || []), ...(t2Data?.buyins || [])].reduce((s, b) => s + (Number(b.amount) || 0), 0);
    const totalCashouts = [...(t1Data?.cashouts || []), ...(t2Data?.cashouts || [])].reduce((s, c) => s + (Number(c.amount) || 0), 0);
    const totalExpenses = allExpenses.reduce((s, e) => s + (Number(e.amount) || 0), 0);
    const totalCollected = allCollected.reduce((s, c) => s + (Number(c.amount) || 0), 0);
    const pool = totalBuyins - totalCashouts;
    const netOnTable = pool - totalExpenses + totalCollected;
    const t1Count = (t1Data?.seatedPlayers || []).filter(Boolean).length;
    const t2Count = (t2Data?.seatedPlayers || []).filter(Boolean).length;

    return (
        <div className="flex flex-col min-h-[100dvh] bg-slate-950 text-white font-outfit">
            <TopNav />
            {gameData && <SessionSubNav sessionId={gameData.id} />}

            {/* ── Header ── */}
            {/* Session Sub-Nav handles all movement now */}

            <div className="flex-1 overflow-y-auto pb-28">
                <div className="max-w-4xl mx-auto w-full px-3 py-4 space-y-4">

                    {/* ── Player Tables ── */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {/* Table 1 */}
                        <div className="bg-slate-900/80 rounded-2xl border border-blue-500/15 overflow-hidden">
                            <div className="flex justify-between items-center px-4 py-3 border-b border-white/5 bg-blue-500/5">
                                <div className="flex items-center gap-2">
                                    <h2 className="text-sm font-black text-blue-300 font-heebo uppercase">{t('tableOne')}</h2>
                                    <button 
                                        onClick={() => setShowPlayerSelector(true)}
                                        className="p-1.5 bg-blue-500/10 text-blue-400 rounded-lg hover:bg-blue-500/30 transition-all border border-blue-500/20"
                                        title="Add player to this session"
                                    >
                                        <Plus className="w-3.5 h-3.5" />
                                    </button>
                                </div>
                                <div className="text-[10px] text-slate-500 font-bold uppercase tracking-wider">
                                    {gameData?.table1Config?.variant?.includes('PLO') ? 'PLO' : 'NLH'} · {gameData?.table1Config?.stakes || '?'} · {t1Count} players
                                </div>
                            </div>
                            <div className="p-2">
                                <PlayerList
                                    players={t1Data?.seatedPlayers || Array(11).fill(null)}
                                    buyins={t1Data?.buyins || []}
                                    cashouts={t1Data?.cashouts || []}
                                    readOnly={false}
                                    moveOnly={true}
                                    onMoveStart={(index) => handleMovePlayer(1, index)}
                                />
                            </div>
                        </div>

                        {/* Table 2 */}
                        <div className="bg-slate-900/80 rounded-2xl border border-purple-500/15 overflow-hidden">
                            <div className="flex justify-between items-center px-4 py-3 border-b border-white/5 bg-purple-500/5">
                                <div className="flex items-center gap-2">
                                    <h2 className="text-sm font-black text-purple-300 font-heebo uppercase">{t('tableTwo')}</h2>
                                    <button 
                                        onClick={() => setShowPlayerSelector(true)}
                                        className="p-1.5 bg-purple-500/10 text-purple-400 rounded-lg hover:bg-purple-500/30 transition-all border border-purple-500/20"
                                        title="Add player to this session"
                                    >
                                        <Plus className="w-3.5 h-3.5" />
                                    </button>
                                </div>
                                <div className="text-[10px] text-slate-500 font-bold uppercase tracking-wider">
                                    {gameData?.table2Config?.variant?.includes('PLO') ? 'PLO' : 'NLH'} · {gameData?.table2Config?.stakes || '?'} · {t2Count} players
                                </div>
                            </div>
                            <div className="p-2">
                                <PlayerList
                                    players={t2Data?.seatedPlayers || Array(11).fill(null)}
                                    buyins={t2Data?.buyins || []}
                                    cashouts={t2Data?.cashouts || []}
                                    readOnly={false}
                                    moveOnly={true}
                                    onMoveStart={(index) => handleMovePlayer(2, index)}
                                />
                            </div>
                        </div>
                    </div>

                    {/* ── Global Expenses ── */}
                    <div className="bg-slate-900/80 rounded-2xl border border-rose-500/15 overflow-hidden">
                        <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
                            <div className="flex items-center gap-2">
                                <div className="w-2 h-2 rounded-full bg-rose-500" />
                                <h3 className="text-sm font-black text-slate-200 font-heebo">{t('globalExpenses')}</h3>
                                {totalExpenses > 0 && (
                                    <span className="text-xs font-black text-rose-400 tabular-nums" dir="ltr">{formatCurrency(totalExpenses)}</span>
                                )}
                            </div>
                            <button
                                onClick={() => { setShowAddCollected(false); setShowAddExpense(v => !v); }}
                                className={clsx(
                                    'flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-black transition-all border',
                                    showAddExpense
                                        ? 'bg-rose-500/20 border-rose-400 text-rose-300'
                                        : 'bg-slate-800 border-slate-700 text-slate-400 hover:border-rose-500/40'
                                )}
                            >
                                {showAddExpense ? <X className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
                                {showAddExpense ? t('cancel') : t('add')}
                            </button>
                        </div>
                        <div className="p-3 space-y-2">
                            {showAddExpense && (
                                <AddFinancialForm
                                    label="Expense"
                                    accentColor="rose"
                                    onAdd={handleAddExpense}
                                    onClose={() => setShowAddExpense(false)}
                                />
                            )}
                            {allExpenses.length === 0 && !showAddExpense ? (
                                <p className="text-xs text-slate-600 text-center py-3 font-heebo">{t('noExpensesLogged')}</p>
                            ) : (
                                allExpenses.map(item => (
                                    <FinancialRow
                                        key={item.id}
                                        item={item}
                                        accentColor="rose"
                                        onDelete={handleDeleteExpense}
                                    />
                                ))
                            )}
                        </div>
                    </div>

                    {/* ── Global Collected (Rake) ── */}
                    <div className="bg-slate-900/80 rounded-2xl border border-emerald-500/15 overflow-hidden">
                        <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
                            <div className="flex items-center gap-2">
                                <div className="w-2 h-2 rounded-full bg-emerald-500" />
                                <h3 className="text-sm font-black text-slate-200 font-heebo">{t('globalCollected')}</h3>
                                {totalCollected > 0 && (
                                    <span className="text-xs font-black text-emerald-400 tabular-nums" dir="ltr">{formatCurrency(totalCollected)}</span>
                                )}
                            </div>
                            <button
                                onClick={() => { setShowAddExpense(false); setShowAddCollected(v => !v); }}
                                className={clsx(
                                    'flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-black transition-all border',
                                    showAddCollected
                                        ? 'bg-emerald-500/20 border-emerald-400 text-emerald-300'
                                        : 'bg-slate-800 border-slate-700 text-slate-400 hover:border-emerald-500/40'
                                )}
                            >
                                {showAddCollected ? <X className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
                                {showAddCollected ? t('cancel') : t('add')}
                            </button>
                        </div>
                        <div className="p-3 space-y-2">
                            {showAddCollected && (
                                <AddFinancialForm
                                    label="Collected"
                                    accentColor="emerald"
                                    onAdd={handleAddCollected}
                                    onClose={() => setShowAddCollected(false)}
                                />
                            )}
                            {allCollected.length === 0 && !showAddCollected ? (
                                <p className="text-xs text-slate-600 text-center py-3 font-heebo">{t('noRakeLogged')}</p>
                            ) : (
                                allCollected.map(item => (
                                    <FinancialRow
                                        key={item.id}
                                        item={item}
                                        accentColor="emerald"
                                        onDelete={handleDeleteCollected}
                                    />
                                ))
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* ── Master Totals Bar (5-column) ── */}
            <div className="fixed bottom-0 inset-x-0 z-50 bg-slate-900/95 backdrop-blur border-t border-white/10">
                <div className="max-w-4xl mx-auto grid grid-cols-5 divide-x divide-white/8">
                    <div className="flex flex-col items-center py-2.5 px-1">
                        <span className="text-[8px] text-slate-600 uppercase tracking-widest font-black leading-tight text-center">{t('buyIns')}</span>
                        <span className="text-xs font-black text-emerald-400 tabular-nums mt-0.5" dir="ltr">{formatCurrency(totalBuyins)}</span>
                    </div>
                    <div className="flex flex-col items-center py-2.5 px-1">
                        <span className="text-[8px] text-slate-600 uppercase tracking-widest font-black leading-tight text-center">{t('expenses')}</span>
                        <span className="text-xs font-black text-rose-400 tabular-nums mt-0.5" dir="ltr">{formatCurrency(totalExpenses)}</span>
                    </div>
                    <div className="flex flex-col items-center py-2.5 px-1">
                        <span className="text-[8px] text-slate-600 uppercase tracking-widest font-black leading-tight text-center">{t('collected')}</span>
                        <span className="text-xs font-black text-cyan-400 tabular-nums mt-0.5" dir="ltr">{formatCurrency(totalCollected)}</span>
                    </div>
                    <div className="flex flex-col items-center py-2.5 px-1">
                        <span className="text-[8px] text-slate-600 uppercase tracking-widest font-black leading-tight text-center">{t('pool')}</span>
                        <span className="text-xs font-black text-amber-400 tabular-nums mt-0.5" dir="ltr">{formatCurrency(pool)}</span>
                    </div>
                    <div className="flex flex-col items-center py-2.5 px-1">
                        <span className="text-[8px] text-slate-600 uppercase tracking-widest font-black leading-tight text-center">{t('netTable')}</span>
                        <span className={clsx('text-xs font-black tabular-nums mt-0.5', netOnTable >= 0 ? 'text-emerald-400' : 'text-rose-400')} dir="ltr">{formatCurrency(netOnTable)}</span>
                    </div>
                </div>
            </div>

            {/* ── End Session Modal ── */}
            {showEndModal && (
                <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
                    <div className="w-full max-w-sm bg-slate-900 border border-rose-500/40 rounded-2xl overflow-hidden shadow-2xl">
                        <div className="p-5 border-b border-rose-500/20 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <StopCircle className="w-5 h-5 text-rose-400" />
                                <h3 className="text-base font-bold text-white font-heebo">{t('endSessionSummary')}</h3>
                            </div>
                            <button onClick={() => setShowEndModal(false)} className="text-slate-600 hover:text-white">
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                        <div className="p-5 space-y-4">
                            {/* Summary Grid */}
                            <div className="grid grid-cols-2 gap-2 text-center">
                                <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl py-3">
                                    <div className="text-[9px] text-blue-400 uppercase font-black tracking-wider">{t('t1Buyins')}</div>
                                    <div className="text-lg font-black text-white mt-0.5" dir="ltr">
                                        {formatCurrency((t1Data?.buyins || []).reduce((s, b) => s + (Number(b.amount) || 0), 0))}
                                    </div>
                                </div>
                                <div className="bg-purple-500/10 border border-purple-500/20 rounded-xl py-3">
                                    <div className="text-[9px] text-purple-400 uppercase font-black tracking-wider">{t('t2Buyins')}</div>
                                    <div className="text-lg font-black text-white mt-0.5" dir="ltr">
                                        {formatCurrency((t2Data?.buyins || []).reduce((s, b) => s + (Number(b.amount) || 0), 0))}
                                    </div>
                                </div>
                                <div className="bg-rose-500/10 border border-rose-500/20 rounded-xl py-3">
                                    <div className="text-[9px] text-rose-400 uppercase font-black tracking-wider">{t('totalExpenses')}</div>
                                    <div className="text-lg font-black text-white mt-0.5" dir="ltr">{formatCurrency(totalExpenses)}</div>
                                </div>
                                <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl py-3">
                                    <div className="text-[9px] text-emerald-400 uppercase font-black tracking-wider">{t('totalCollected')}</div>
                                    <div className="text-lg font-black text-white mt-0.5" dir="ltr">{formatCurrency(totalCollected)}</div>
                                </div>
                            </div>
                            <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl py-3 text-center">
                                <div className="text-[9px] text-amber-400 uppercase font-black tracking-wider">{t('netPoolRemaining')}</div>
                                <div className="text-2xl font-black text-amber-400 mt-0.5" dir="ltr">{formatCurrency(netOnTable)}</div>
                                <div className="text-[10px] text-slate-500 mt-1">{t('totalPlayersTables', { count: t1Count + t2Count, tables: t1Count > 0 && t2Count > 0 ? 2 : 1 })}</div>
                            </div>
                            <p className="text-slate-400 text-xs font-heebo leading-relaxed text-center">
                                {t('archiveWarning')}
                            </p>
                            <div className="grid grid-cols-2 gap-3">
                                <button
                                    onClick={() => setShowEndModal(false)}
                                    className="py-3 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 text-sm font-bold border border-slate-700 transition-colors font-heebo"
                                >
                                    {t('cancel')}
                                </button>
                                <button
                                    onClick={handleEndSession}
                                    disabled={ending}
                                    className="py-3 rounded-xl bg-rose-600 hover:bg-rose-500 text-white text-sm font-bold transition-colors disabled:opacity-50 font-heebo"
                                >
                                    {ending ? t('ending') : t('confirmEnd')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Player Selection Modal ── */}
            {showPlayerSelector && (
                <PlayerSelector
                    players={allPlayers}
                    onSelect={handlePlayerSelected}
                    onClose={() => setShowPlayerSelector(false)}
                    onCreatePlayer={async (name) => {
                        const newPlayerRef = doc(collection(db, `hosts/${hostId}/players`));
                        const newPlayer = {
                            id: newPlayerRef.id,
                            name,
                            roles: ['Player'],
                            totalSessions: 0,
                            totalBuyIn: 0,
                            totalCashout: 0,
                            totalProfitLoss: 0,
                            createdAt: new Date().toISOString()
                        };
                        await setDoc(newPlayerRef, newPlayer);
                        handlePlayerSelected(newPlayer);
                    }}
                />
            )}

            {/* ── Table Choice Modal ── */}
            {showTableSelect && (
                <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/90 backdrop-blur-md p-4">
                    <div className="w-full max-w-xs bg-slate-900 border border-amber-500/30 rounded-[2rem] p-6 shadow-2xl">
                        <h3 className="text-xl font-black text-white text-center mb-6 font-heebo">{t('selectTableFor', { name: pendingPlayer?.name })}</h3>
                        <div className="grid grid-cols-1 gap-4">
                            <button
                                onClick={() => handleTableAssign(1)}
                                className="group relative overflow-hidden py-6 rounded-2xl bg-blue-500/10 border border-blue-500/30 text-blue-400 font-black text-lg transition-all hover:bg-blue-500 hover:text-slate-950 hover:scale-105 active:scale-95"
                            >
                                <span className="relative z-10">{t('tableOne')}</span>
                                <div className="absolute inset-0 bg-blue-500 opacity-0 group-hover:opacity-10 transition-opacity" />
                            </button>
                            <button
                                onClick={() => handleTableAssign(2)}
                                className="group relative overflow-hidden py-6 rounded-2xl bg-purple-500/10 border border-purple-500/30 text-purple-400 font-black text-lg transition-all hover:bg-purple-500 hover:text-slate-950 hover:scale-105 active:scale-95"
                            >
                                <span className="relative z-10">{t('tableTwo')}</span>
                                <div className="absolute inset-0 bg-purple-500 opacity-0 group-hover:opacity-10 transition-opacity" />
                            </button>
                        </div>
                        <button
                            onClick={() => { setShowTableSelect(false); setPendingPlayer(null); }}
                            className="w-full mt-6 py-3 text-slate-500 font-bold hover:text-white transition-colors"
                        >
                            {t('cancel')}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};
